import { useReducer, useCallback } from "react";
import type { Pos, Direction, FuelGrid, GameState, Puzzle } from "../types.ts";
import { cloneGrid } from "../engine/grid.ts";
import { canStartAt, canMoveTo, applyStart, applyMove, applyDirectionMove } from "../engine/movement.ts";
import { calculateStars } from "../engine/scoring.ts";

type Action =
  | { type: "SELECT_START"; pos: Pos }
  | { type: "MOVE"; pos: Pos }
  | { type: "MOVE_DIR"; dir: Direction }
  | { type: "UNDO" }
  | { type: "RESET" };

interface Snapshot {
  grid: FuelGrid;
  playerPos: Pos;
  steps: number;
}

let snapshots: Snapshot[] = [];

function createInitialState(puzzle: Puzzle): GameState {
  snapshots = [];
  return {
    puzzle,
    grid: cloneGrid(puzzle.grid),
    playerPos: null,
    phase: "starting",
    steps: 0,
    undoCount: 0,
    startPos: null,
    startedAt: Date.now(),
    completedAt: null,
    stars: 0,
  };
}

function reducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case "SELECT_START": {
      if (state.phase !== "starting") return state;
      if (!canStartAt(state.grid, action.pos)) return state;

      const result = applyStart(state.grid, action.pos);

      if (result.won) {
        const stars = calculateStars(1, state.puzzle.optimalSteps, 0);
        return {
          ...state,
          grid: result.grid,
          playerPos: result.playerPos,
          phase: "won",
          steps: 1,
          startPos: action.pos,
          completedAt: Date.now(),
          stars,
        };
      }

      if (result.stuck) {
        return {
          ...state,
          grid: result.grid,
          playerPos: result.playerPos,
          phase: "stuck",
          steps: 1,
          startPos: action.pos,
        };
      }

      snapshots = [];
      return {
        ...state,
        grid: result.grid,
        playerPos: result.playerPos,
        phase: "playing",
        steps: 1,
        startPos: action.pos,
      };
    }

    case "MOVE": {
      if (state.phase !== "playing" || !state.playerPos) return state;
      if (!canMoveTo(state.grid, state.playerPos, action.pos)) return state;

      snapshots.push({
        grid: cloneGrid(state.grid),
        playerPos: state.playerPos,
        steps: state.steps,
      });

      const result = applyMove(state.grid, state.playerPos, action.pos);
      const newSteps = state.steps + 1;

      if (result.won) {
        const stars = calculateStars(newSteps, state.puzzle.optimalSteps, state.undoCount);
        return { ...state, grid: result.grid, playerPos: result.playerPos, phase: "won", steps: newSteps, completedAt: Date.now(), stars };
      }

      if (result.stuck) {
        return { ...state, grid: result.grid, playerPos: result.playerPos, phase: "stuck", steps: newSteps };
      }

      return { ...state, grid: result.grid, playerPos: result.playerPos, steps: newSteps };
    }

    case "MOVE_DIR": {
      if (state.phase !== "playing" || !state.playerPos) return state;

      const result = applyDirectionMove(state.grid, state.playerPos, action.dir);
      if (!result) return state;

      snapshots.push({
        grid: cloneGrid(state.grid),
        playerPos: state.playerPos,
        steps: state.steps,
      });

      const newSteps = state.steps + 1;

      if (result.won) {
        const stars = calculateStars(newSteps, state.puzzle.optimalSteps, state.undoCount);
        return { ...state, grid: result.grid, playerPos: result.playerPos, phase: "won", steps: newSteps, completedAt: Date.now(), stars };
      }

      if (result.stuck) {
        return { ...state, grid: result.grid, playerPos: result.playerPos, phase: "stuck", steps: newSteps };
      }

      return { ...state, grid: result.grid, playerPos: result.playerPos, steps: newSteps };
    }

    case "UNDO": {
      if (snapshots.length === 0) return state;
      if (state.phase === "won") return state;

      const snapshot = snapshots.pop()!;
      return {
        ...state,
        grid: snapshot.grid,
        playerPos: snapshot.playerPos,
        steps: snapshot.steps,
        phase: snapshot.steps === 0 ? "starting" : "playing",
        undoCount: state.undoCount + 1,
      };
    }

    case "RESET": {
      return createInitialState(state.puzzle);
    }

    default:
      return state;
  }
}

export function useGame(puzzle: Puzzle) {
  const [state, dispatch] = useReducer(reducer, puzzle, createInitialState);

  const selectStart = useCallback((pos: Pos) => dispatch({ type: "SELECT_START", pos }), []);
  const move = useCallback((pos: Pos) => dispatch({ type: "MOVE", pos }), []);
  const moveDir = useCallback((dir: Direction) => dispatch({ type: "MOVE_DIR", dir }), []);
  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  return { state, selectStart, move, moveDir, undo, reset };
}
